import { useEffect, useState } from 'react';
import { Archive, Buildings, CalendarPlus, Plus, X } from '@phosphor-icons/react';
import { useWorkspace } from '../App';
import { api, ApiError } from '../lib/api';
import { accessLabel, faNumber, jalaliDate, statusLabel } from '../lib/format';
import {
  companyFieldHints, describeCompanyField, finalizeCompanyField,
  normalizeCompanyField, validateCompanyField, type CompanyFieldKey,
} from '../lib/form-utils';
import { Card, InlineNotice, PageHeader, StatusBadge } from '../components/Ui';
import { ScoreRing } from '../components/Charts';
import { CustomSelect, SmartDateField, SmartInput } from '../components/SmartForm';
import type { AccessLevel, Company, FiscalYear } from '../types';

/** Identity fields run the check-digit validators; the rest are free text. */
const IDENTITY_FIELDS: CompanyFieldKey[] = ['name', 'legalName', 'nationalId', 'economicCode', 'registrationNumber'];

const PROFILE_FIELDS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'activityType', label: 'نوع فعالیت', hint: 'مثلاً تولیدی، بازرگانی، خدماتی یا پیمانکاری.' },
  { key: 'province', label: 'استان', hint: 'استان محل ثبت یا فعالیت اصلی.' },
  { key: 'city', label: 'شهر', hint: 'شهر محل دفتر مرکزی.' },
  { key: 'phone', label: 'تلفن', hint: 'شماره تماس دفتر مرکزی.' },
  { key: 'email', label: 'ایمیل', hint: 'ایمیل رسمی شرکت برای مکاتبات.' },
  { key: 'website', label: 'وب‌سایت', hint: 'نشانی وب‌سایت رسمی، در صورت وجود.' },
  { key: 'chiefExecutive', label: 'مدیرعامل', hint: 'نام مدیرعامل ثبت‌شده.' },
  { key: 'financialManager', label: 'مدیر مالی', hint: 'نام مدیر مالی یا حسابدار مسئول.' },
  { key: 'address', label: 'نشانی', hint: 'نشانی کامل دفتر مرکزی.' },
];

const EMPTY: Record<string, string> = Object.fromEntries(
  [...IDENTITY_FIELDS, ...PROFILE_FIELDS.map((field) => field.key), 'notes'].map((key) => [key, '']),
);

export function CompaniesPage() {
  const { companies, company, bootstrap, can, refresh, selectCompany, navigate } = useWorkspace();
  const [form, setForm] = useState<Record<string, string>>({ ...EMPTY });
  const [currency, setCurrency] = useState<'toman' | 'rial'>('rial');
  const [fiscalStart, setFiscalStart] = useState(bootstrap.settings.defaultFiscalYearStartIso);
  const [editingId, setEditingId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  // A Persian accounting app asking for a Gregorian date through
  // `window.prompt` was the one place the product dropped out of its own
  // language and layout; both prompts are real dialogs now.
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [newYearStart, setNewYearStart] = useState('');
  const [archiveTarget, setArchiveTarget] = useState<Company | null>(null);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<{
    company: Company; fiscalYears: FiscalYear[]; access: AccessLevel;
    grants: Array<{ userId: string; username: string; displayName: string; role: string; level: AccessLevel }>;
  } | null>(null);

  useEffect(() => {
    if (!company) { setDetail(null); return; }
    void api.company(company.id).then(setDetail).catch(() => setDetail(null));
  }, [company]);

  function openCreate() {
    setForm({ ...EMPTY });
    setCurrency('rial');
    setFiscalStart(bootstrap.settings.defaultFiscalYearStartIso);
    setEditingId('');
    setTouched(false);
    setError('');
    setDialogOpen(true);
  }

  /** Offers the day after the latest year ends, which is the answer in
   *  practice — fiscal years abut. */
  function openYearDialog() {
    const latestEnd = (detail?.fiscalYears ?? [])
      .map((year) => year.endIso)
      .sort()
      .at(-1);
    const nextStart = latestEnd
      ? new Date(`${latestEnd.slice(0, 10)}T00:00:00Z`)
      : null;
    if (nextStart) nextStart.setUTCDate(nextStart.getUTCDate() + 1);
    setNewYearStart(nextStart ? nextStart.toISOString().slice(0, 10) : bootstrap.settings.defaultFiscalYearStartIso);
    setError('');
    setYearDialogOpen(true);
  }

  function openEdit(target: Company) {
    setForm({
      ...EMPTY,
      ...Object.fromEntries(Object.keys(EMPTY).map((key) => [key, String((target as unknown as Record<string, unknown>)[key] ?? '')])),
    });
    setCurrency(target.reportingCurrency);
    setEditingId(target.id);
    setTouched(false);
    setError('');
    setDialogOpen(true);
  }

  const identityErrors = Object.fromEntries(
    IDENTITY_FIELDS.map((field) => [field, validateCompanyField(field, form[field] ?? '')]),
  ) as Record<CompanyFieldKey, string>;
  const invalid = IDENTITY_FIELDS.some((field) => identityErrors[field]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (invalid) return;
    setError('');

    const payload: Record<string, unknown> = { reportingCurrency: currency };
    for (const field of IDENTITY_FIELDS) payload[field] = finalizeCompanyField(field, form[field] ?? '');
    for (const field of PROFILE_FIELDS) payload[field.key] = (form[field.key] ?? '').trim();
    payload.notes = (form.notes ?? '').trim();

    try {
      if (editingId) {
        const current = companies.find((item) => item.id === editingId);
        await api.updateCompany(editingId, { ...payload, expectedVersion: current?.version ?? 0 });
      } else {
        const created = await api.createCompany({ ...payload, fiscalYearStartIso: fiscalStart });
        selectCompany(created.company.id);
      }
      setDialogOpen(false);
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'ثبت اطلاعات انجام نشد.');
    }
  }

  async function addFiscalYear() {
    if (!company) return;
    setError('');
    try {
      await api.createFiscalYear(company.id, newYearStart);
      setDetail(await api.company(company.id));
      await refresh();
      setYearDialogOpen(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'سال مالی ساخته نشد.');
    }
  }

  async function toggleFiscalYear(year: FiscalYear) {
    if (!company) return;
    try {
      await api.setFiscalYearStatus(company.id, year.id, year.status === 'open' ? 'closed' : 'open');
      setDetail(await api.company(company.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'وضعیت سال مالی تغییر نکرد.');
    }
  }

  async function archive() {
    if (!archiveTarget) return;
    setError('');
    try {
      await api.archiveCompany(archiveTarget.id, archiveTarget.version);
      await refresh();
      setArchiveTarget(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'بایگانی انجام نشد.');
    }
  }

  async function setGrant(userId: string, level: AccessLevel) {
    if (!company) return;
    try {
      await api.setCompanyAccess(company.id, userId, level);
      setDetail(await api.company(company.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'سطح دسترسی تغییر نکرد.');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="شرکت‌ها"
        title="شرکت‌های تحت تحلیل"
        description="هر شرکت سال مالی، حساب‌ها و گزارش‌های خودش را دارد و داده‌هایشان با هم مخلوط نمی‌شود."
        actions={can('company.write') ? (
          <button className="button button--primary" onClick={openCreate}><Plus size={16} /> شرکت جدید</button>
        ) : undefined}
      />

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}

      <div className="company-grid">
        {companies.map((item) => (
          <Card key={item.id} className={item.id === company?.id ? 'company-card company-card--active' : 'company-card'} interactive>
            <div className="company-card__head">
              <span className="company-icon"><Buildings size={20} weight="fill" /></span>
              <div>
                <strong>{item.name}</strong>
                <small>{item.legalName || item.activityType || 'بدون نام حقوقی ثبت‌شده'}</small>
              </div>
              <StatusBadge status={item.status} label={statusLabel(item.status)} />
            </div>

            {/* §۳ وضعیت تحلیل — کیفیت اطلاعات و درصد تکمیل داده, per company.
                A company nobody has processed shows no ring at all rather than
                an empty one: zero quality and no data are different claims. */}
            {item.analysis?.quality && (
              <ScoreRing
                score={item.analysis.quality.score}
                labelFa="کیفیت اطلاعات"
                size={96}
                captionFa={item.analysis.quality.completeness === null
                  ? 'حسابی برای سنجش طبقه‌بندی ثبت نشده است.'
                  : `${faNumber(Math.round(item.analysis.quality.completeness * 100))}٪ تکمیل داده`}
              />
            )}

            <div className="health-list">
              {item.nationalId && <div className="health-list__row"><span>شناسه ملی</span><b dir="ltr">{item.nationalId}</b></div>}
              {item.counts && <div className="health-list__row"><span>ردیف‌های دفتر</span><b>{faNumber(item.counts.entries)}</b></div>}
              {item.counts && <div className="health-list__row"><span>گزارش‌ها</span><b>{faNumber(item.counts.reports)}</b></div>}
              <div className="health-list__row">
                <span>آخرین پردازش</span>
                <b>{item.analysis?.lastRunAt
                  ? `${statusLabel(item.analysis.lastRunStatus ?? '')} — ${jalaliDate(item.analysis.lastRunAt)}`
                  : 'انجام نشده'}</b>
              </div>
              {item.analysis && item.analysis.openIssues > 0 && (
                <div className="health-list__row">
                  <span>یافته‌های باز کیفیت</span>
                  <b>{faNumber(item.analysis.openIssues)} مورد</b>
                </div>
              )}
              <div className="health-list__row"><span>دسترسی شما</span><b>{accessLabel(item.access)}</b></div>
            </div>

            <div className="company-card__actions">
              <button className="button button--soft button--small" onClick={() => { selectCompany(item.id); navigate('dashboard'); }}>
                انتخاب و مشاهده
              </button>
              {can('company.write') && (
                <button className="button button--ghost button--small" onClick={() => openEdit(item)}>ویرایش</button>
              )}
              {can('company.archive') && item.status === 'active' && (
                <button className="button button--ghost button--small" onClick={() => setArchiveTarget(item)}><Archive size={15} /> بایگانی</button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {detail && (
        <div className="detail-grid">
          <Card>
            <div className="card-heading">
              <h2>سال‌های مالی {detail.company.name}</h2>
              {can('fiscal.write') && (
                <button className="button button--ghost button--small" onClick={openYearDialog}>
                  <CalendarPlus size={15} /> سال مالی جدید
                </button>
              )}
            </div>
            <div className="health-list">
              {detail.fiscalYears.map((year) => (
                <div key={year.id} className="health-list__row">
                  <span>{year.labelFa}</span>
                  <b>{jalaliDate(year.startIso)} تا {jalaliDate(year.endIso)}</b>
                  {can('fiscal.write') && (
                    <button className="button button--ghost button--small" onClick={() => toggleFiscalYear(year)}>
                      {year.status === 'open' ? 'بستن سال' : 'بازگشایی'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {can('users.read') && detail.grants.length > 0 && (
            <Card>
              <div className="card-heading"><h2>دسترسی‌ها</h2></div>
              <p className="hint">
                سطح دسترسی هر نفر روی این شرکت. دسترسی نمی‌تواند از نقش سازمانی فرد بیشتر شود — فقط
                می‌تواند آن را محدودتر کند.
              </p>
              <div className="health-list">
                {detail.grants.map((grant) => (
                  <div key={grant.userId} className="health-list__row">
                    <span>{grant.displayName || grant.username}</span>
                    <CustomSelect
                      ariaLabel={`دسترسی ${grant.displayName || grant.username}`}
                      value={grant.level}
                      disabled={!can('users.write')}
                      onChange={(level) => setGrant(grant.userId, level as AccessLevel)}
                      options={(['none', 'read', 'write', 'admin'] as AccessLevel[]).map((level) => ({
                        value: level, label: accessLabel(level),
                      }))}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {dialogOpen && (
        <div className="dialog-overlay" role="dialog" aria-modal="true" aria-label={editingId ? 'ویرایش شرکت' : 'ثبت شرکت جدید'}>
          <div className="dialog-content">
            <div className="dialog-head">
              <h2>{editingId ? 'ویرایش اطلاعات شرکت' : 'ثبت شرکت جدید'}</h2>
              <button className="icon-button dialog-head__close" onClick={() => setDialogOpen(false)} aria-label="بستن"><X /></button>
            </div>

            <form className="wizard-form" onSubmit={submit}>
              <div className="form-grid">
                {IDENTITY_FIELDS.map((field) => (
                  <SmartInput
                    key={field}
                    id={`company-${field}`}
                    label={companyFieldHints[field] ? labelOf(field) : field}
                    hint={companyFieldHints[field]}
                    error={identityErrors[field]}
                    success={describeCompanyField(field, form[field] ?? '')}
                    showValidation={touched}
                    required={field === 'name'}
                    code={field !== 'name' && field !== 'legalName'}
                    wide={field === 'name' || field === 'legalName'}
                    value={form[field] ?? ''}
                    onValueChange={(value) => setForm((current) => ({ ...current, [field]: normalizeCompanyField(field, value) }))}
                  />
                ))}

                {PROFILE_FIELDS.map((field) => (
                  <SmartInput
                    key={field.key}
                    id={`company-${field.key}`}
                    label={field.label}
                    hint={field.hint}
                    wide={field.key === 'address'}
                    value={form[field.key] ?? ''}
                    onValueChange={(value) => setForm((current) => ({ ...current, [field.key]: value }))}
                  />
                ))}

                <div className="field">
                  <span className="smart-field__label">واحد پول گزارش‌دهی</span>
                  <CustomSelect
                    ariaLabel="واحد پول گزارش‌دهی"
                    value={currency}
                    onChange={(value) => setCurrency(value as 'toman' | 'rial')}
                    options={[{ value: 'rial', label: 'ریال' }, { value: 'toman', label: 'تومان' }]}
                  />
                </div>

                {!editingId && (
                  // Inline for the same reason as the year dialog: this dialog
                  // is its own scroll box and a popover opening near its
                  // bottom edge gets cut off rather than escaping it.
                  <SmartDateField
                    id="company-fiscal-start"
                    label="شروع سال مالی"
                    hint="روز آغاز نخستین سال مالی شرکت؛ معمولاً ۱ فروردین."
                    inline
                    value={fiscalStart}
                    onChange={setFiscalStart}
                  />
                )}
              </div>

              <div className="dialog-actions">
                <button className="button button--ghost" type="button" onClick={() => setDialogOpen(false)}>انصراف</button>
                <button className="button button--primary" type="submit">{editingId ? 'ذخیره تغییرات' : 'ثبت شرکت'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {yearDialogOpen && (
        <div className="dialog-overlay" role="dialog" aria-modal="true" aria-label="سال مالی جدید">
          <div className="dialog-content dialog-content--narrow">
            <div className="dialog-head">
              <div>
                <h2>سال مالی جدید</h2>
                <p>سال مالی از این تاریخ آغاز می‌شود و دقیقاً یک سال بعد پایان می‌یابد.</p>
              </div>
              <button className="icon-button dialog-head__close" onClick={() => setYearDialogOpen(false)} aria-label="بستن"><X /></button>
            </div>
            <form className="wizard-form" onSubmit={(event) => { event.preventDefault(); void addFiscalYear(); }}>
              {/* Inline: this dialog is its own scroll box, so a popover
                  calendar would open inside it and be clipped. */}
              <SmartDateField
                id="new-fiscal-start"
                label="تاریخ شروع"
                hint="سال مالی از این روز آغاز و دقیقاً یک سال بعد بسته می‌شود."
                wide
                inline
                value={newYearStart}
                onChange={setNewYearStart}
              />
              <div className="dialog-actions">
                <button className="button button--ghost" type="button" onClick={() => setYearDialogOpen(false)}>انصراف</button>
                <button className="button button--primary" type="submit" disabled={!newYearStart.trim()}>ساخت سال مالی</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {archiveTarget && (
        <div className="dialog-overlay" role="dialog" aria-modal="true" aria-label="تأیید بایگانی">
          <div className="dialog-content dialog-content--narrow">
            <div className="dialog-head">
              <div>
                <h2>بایگانی «{archiveTarget.name}»؟</h2>
                <p>داده‌های این شرکت حذف نمی‌شود؛ فقط از فهرست شرکت‌های فعال خارج می‌گردد.</p>
              </div>
              <button className="icon-button dialog-head__close" onClick={() => setArchiveTarget(null)} aria-label="بستن"><X /></button>
            </div>
            <div className="dialog-actions">
              <button className="button button--ghost" type="button" onClick={() => setArchiveTarget(null)}>انصراف</button>
              <button className="button button--primary" type="button" onClick={() => void archive()}>
                <Archive size={15} /> بایگانی کن
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function labelOf(field: CompanyFieldKey): string {
  return ({
    name: 'نام شرکت',
    legalName: 'نام حقوقی کامل',
    nationalId: 'شناسه ملی',
    economicCode: 'کد اقتصادی',
    registrationNumber: 'شماره ثبت',
  } as Record<CompanyFieldKey, string>)[field];
}
